<template>
  <div class="supplier-page">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Đang tải dữ liệu...</p>
    </div>
    <div class="supplier-header">
      <h1>Quản lý Nhà cung cấp</h1>
      <div class="button-action">
        <button @click="viewCreateSupplier" class="btn-add">+ Thêm NCC</button>
      </div>
    </div>
    <!-- FILTER -->
    <div class="supplier-filter">
      <input v-model="search" type="text" placeholder="Tìm kiếm ncc..." />

      <select v-model="status">
        <option value="">Tất cả trạng thái</option>
        <option value="1">Hoạt động</option>
        <option value="0">Tạm ngưng</option>
      </select>

      <button class="btn-search" @click="fetchSuppliers">Tìm kiếm</button>

      <!-- Toggle columns -->
      <div class="column-toggle">
        <button @click="showColumnPanel = !showColumnPanel">
          ⚙️ Cột hiển thị
        </button>

        <div v-if="showColumnPanel" class="column-panel">
          <label v-for="col in columns" :key="col.key">
            <input type="checkbox" v-model="col.visible" />
            {{ col.label }}
          </label>
        </div>
      </div>
    </div>

    <!-- TABLE -->
    <div class="supplier-table">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th v-for="col in visibleColumns" :key="col.key">
              {{ col.label }}
            </th>
            <th>Hành động</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in suppliers" :key="item.id">
            <td>{{ index + 1 }}</td>

            <td
              v-for="col in visibleColumns"
              :key="col.key"
              v-html="renderValue(item, col)"
            ></td>

            <td>
              <button class="btn-edit" @click="viewDetailSupplier(item)">
                Xem
              </button>
            </td>
          </tr>

          <tr v-if="!suppliers.length">
            <td colspan="100%" class="empty">Không có dữ liệu</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="pagination.total > 0" class="pagination">
      <div class="pagination-info">
        Hiển thị {{ pagination.from }} - {{ pagination.to }} trong tổng số
        {{ pagination.total }} khách hàng
      </div>
      <div class="pagination-controls">
        <button
          @click="changePage(pagination.current_page - 1)"
          :disabled="pagination.current_page === 1"
          class="btn btn-outline btn-sm"
        >
          Trước
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="changePage(page)"
          :class="[
            'btn',
            'btn-sm',
            page === pagination.current_page ? 'btn-primary' : 'btn-outline',
          ]"
        >
          {{ page }}
        </button>

        <button
          @click="changePage(pagination.current_page + 1)"
          :disabled="pagination.current_page === pagination.last_page"
          class="btn btn-outline btn-sm"
        >
          Sau
        </button>
      </div>
    </div>

    <CreateSupplierModal
      v-if="showCreateModal"
      @save="saveSupplier"
      @close="showCreateModal = false"
    />

    <DetailSupplierModal
      v-if="showDetailModal"
      :supplier="selectedSupplier"
      @close="showDetailModal = false"
      @save="fetchSuppliers"
    />
  </div>
</template>
<script setup>
import { useToast } from "@/composables/useToast";
import { supplierApi } from "@/services/api";
import { computed, onMounted, ref } from "vue";
import { formatCurrency } from "@/utils/helpers";
import { usePagination } from "@/composables/usePagination";
import CreateSupplierModal from "@/components/modal/suppliers/CreateSupplierModal.vue";
import DetailSupplierModal from "@/components/modal/suppliers/DetailSupplierModal.vue";

// const fileInput = ref(null);
const toast = useToast();
const loading = ref(false);
const suppliers = ref([]);
const search = ref("");
const status = ref("");
const showColumnPanel = ref(false);
const showCreateModal = ref(false);
// const selectedsupplier = ref(null);
const showDetailModal = ref(false);
const selectedSupplier = ref(null);
const columns = ref([
  { key: "id", label: "ID", visible: false },
  { key: "ma_ncc", label: "Mã NCC", visible: true },
  { key: "name", label: "Tên NCC", visible: true },
  { key: "phone", label: "Điện thoại", visible: true },
  { key: "address", label: "Địa chỉ", visible: true },
  { key: "email", label: "Email", visible: false },
  { key: "no_can_tra", label: "Nợ cần trả", visible: true, type: "currency" },
  { key: "nhom_ncc", label: "Nhóm NCC", visible: false },
  { key: "ghi_chu", label: "Ghi chú", visible: false },
  { key: "trang_thai", label: "Trạng thái", visible: false, type: "status" },
]);
const renderValue = (item, col) => {
  const value = item[col.key];
  if (col.type === "currency") {
    return formatCurrency(value);
  }
  if (col.type === "status") {
    const s = formatStatus(value);
    return `<span class="status-badge ${s.class}">${s.text}</span>`;
  }

  return value || "-";
};
const formatStatus = (status) => {
  const statusMap = {
    1: { text: "Hoạt động", class: "status-active" },
    0: { text: "Tạm ngưng", class: "status-inactive" },
  };

  return statusMap[status] || { text: "-", class: "status-unknown" };
};

const visibleColumns = computed(() =>
  columns.value.filter((col) => col.visible),
);

const fetchSuppliers = async () => {
  loading.value = true;
  try {
    const response = await supplierApi.getSuppliers({
      page: pagination.current_page,
      search: search.value,
      per_page: pagination.per_page || 15,
    });
    suppliers.value = response.data.data.data || response.data;
    const { ...paginationMeta } = response.data.data;
    updatePagination(paginationMeta);
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || "Lỗi tải dữ liệu nhà cung cấp";
    toast.error(errorMessage);
    console.log(error);
  } finally {
    loading.value = false;
  }
};

const viewCreateSupplier = () => {
  showCreateModal.value = true;
};

const viewDetailSupplier = (supplier) => {
  selectedSupplier.value = supplier;
  showDetailModal.value = true;
};

const saveSupplier = () => {
  showCreateModal.value = false;
  fetchSuppliers();
};
const { pagination, changePage, visiblePages, updatePagination } =
  usePagination(fetchSuppliers);

onMounted(fetchSuppliers);
</script>

<style scoped>
/* ===== PAGE ===== */
.supplier-page {
  background: #f5f6fa;
  padding: 20px;
  border-radius: 12px;
}

/* ===== HEADER ===== */
.supplier-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.supplier-header h1 {
  font-size: 22px;
  font-weight: 700;
}

.btn-add {
  background: linear-gradient(135deg, #f36c21, #ffd54f);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 20px;
  cursor: pointer;
  transition: 0.3s;
}

.btn-add:hover {
  transform: scale(1.05);
}

/* ===== FILTER ===== */
.supplier-filter {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.supplier-filter input,
.supplier-filter select,
.column-toggle {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  outline: none;
}

.btn-search {
  background: #333;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
}

.column-toggle {
  position: relative;
}
.column-panel {
  position: absolute;
  top: 35px;
  right: 0;
  width: 250px;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ddd;
  padding: 10px;
}
.column-panel label {
  display: block;
  margin-bottom: 5px;
}

/* ===== TABLE ===== */
.supplier-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #111;
  color: white;
}

th,
td {
  padding: 12px 14px;
  text-align: left;
  font-size: 14px;
}

tbody tr {
  border-bottom: 1px solid #eee;
}

tbody tr:hover {
  background: #f9f9f9;
}

/* ===== IMAGE ===== */
.supplier-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
}

/* ===== STATUS ===== */
/* .status-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background-color: #dcfce7;
  color: #166534;
}

.status-inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-unknown {
  background-color: #f3f4f6;
  color: #4b5563;
} */

/* ===== ACTION BUTTON ===== */
/* .btn-edit {
  background: #2196f3;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
  margin-right: 5px;
}

.btn-delete {
  background: #f44336;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
} */
</style>
